<template>
    <v-app>
    <!-- Main Content -->
    <router-view />

    <!-- Enhanced Global Notification System -->
    <v-snackbar
      v-model="notification.show"
      :color="notification.color"
      :timeout="notification.timeout"
      location="top right"
      :multi-line="!!notification.details"
      :vertical="!!notification.actions && notification.actions.length > 0"
      min-width="320"
      max-width="500"
      elevation="6"
      class="enhanced-notification"
    >
      <div class="d-flex align-start">
        <!-- Icon -->
        <v-icon 
          v-if="notification.icon"
          :icon="notification.icon"
          class="mr-3 mt-1"
          size="20"
        />
        
        <!-- Content -->
        <div class="flex-grow-1">
          <!-- Title -->
          <div 
            v-if="notification.title"
            class="font-weight-medium mb-1"
            style="line-height: 1.2;"
          >
            {{ notification.title }}
          </div>
          
          <!-- Message -->
          <div 
            class="text-body-2"
            :class="{ 'text-medium-emphasis': notification.title }"
            style="line-height: 1.3;"
          >
            {{ notification.message }}
          </div>
          
          <!-- Details -->
          <div 
            v-if="notification.details"
            class="text-caption text-medium-emphasis mt-1"
            style="line-height: 1.2;"
          >
            {{ notification.details }}
          </div>
          
          <!-- Timestamp -->
          <div 
            v-if="notification.timestamp"
            class="text-caption text-disabled mt-1"
            style="line-height: 1.1;"
          >
            {{ formatNotificationTime(notification.timestamp) }}
          </div>
        </div>
        
        <!-- Close Button -->
        <v-btn
          v-if="notification.persistent"
          icon="mdi-close"
          size="small"
          variant="text"
          @click="notification.show = false"
          class="ml-2"
        />
      </div>
      
      <!-- Action Buttons -->
      <template v-if="notification.actions && notification.actions.length > 0" #actions>
        <div class="d-flex gap-2 mt-2">
          <v-btn
            v-for="action in notification.actions"
            :key="action.label"
            :color="action.color || 'primary'"
            :variant="action.variant || 'text'"
            size="small"
            @click="handleNotificationAction(action)"
          >
            {{ action.label }}
          </v-btn>
        </div>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { onMounted, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useEventBus } from '@/composables/useEventBus'
import { dateFormat } from '@/utils/dateFormat'

export default {
  name: 'App',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    const { on } = useEventBus()

    // Enhanced notification state
    const notification = reactive({
      show: false,
      message: '',
      title: null,
      color: 'info',
      icon: null,
      timeout: 4000,
      persistent: false,
      details: null,
      actions: null,
      timestamp: null
    })

    // Listen for enhanced global notifications
    on('show-notification', (payload) => {
      notification.message = payload.message
      notification.title = payload.title || null
      notification.color = payload.color || 'info'
      notification.icon = payload.icon || null
      notification.timeout = payload.persistent ? 0 : (payload.timeout || 4000)
      notification.persistent = payload.persistent || false
      notification.details = payload.details || null
      notification.actions = payload.actions || null
      notification.timestamp = payload.timestamp || new Date().toISOString()
      notification.show = true
    })

    // Format notification timestamp
    const formatNotificationTime = (timestamp) => {
      if (!timestamp) return ''
      const now = new Date()
      const notificationTime = new Date(timestamp)
      const diffMs = now.getTime() - notificationTime.getTime()
      const diffSeconds = Math.floor(diffMs / 1000)
      const diffMinutes = Math.floor(diffSeconds / 60)
      
      if (diffSeconds < 60) return 'Just now'
      if (diffMinutes < 60) return `${diffMinutes}m ago`
      
      return dateFormat.formatTime(timestamp)
    }

    // Handle notification action clicks
    const handleNotificationAction = (action) => {
      if (action.callback) {
        action.callback()
      }
      if (action.closeAfterClick !== false) {
        notification.show = false
      }
    }

    onMounted(async () => {
      // Initialize auth state
      if (authStore.token && !authStore.user) {
        try {
          await authStore.getCurrentUser()
        } catch (error) {
          console.warn('Failed to get current user on app init:', error)
        }
      }
    })

    return {
      notification,
      formatNotificationTime,
      handleNotificationAction
    }
  }
}
</script>

<style>
.v-application {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif !important;
}

/* Enhanced notification styling */
.enhanced-notification .v-snackbar__content {
  padding: 16px 20px !important;
}

.enhanced-notification .v-snackbar__wrapper {
  box-shadow: 0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
  backdrop-filter: blur(16px);
}

.enhanced-notification .v-icon {
  opacity: 0.9;
}

/* Notification animations */
.enhanced-notification.v-snackbar--active {
  animation: slideInFromRight 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.enhanced-notification .v-snackbar__wrapper {
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes slideInFromRight {
  0% {
    transform: translateX(120%) scale(0.95);
    opacity: 0;
  }
  60% {
    transform: translateX(-5%) scale(1.02);
    opacity: 0.9;
  }
  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
}

/* Hover effects */
.enhanced-notification .v-snackbar__wrapper:hover {
  transform: scale(1.02);
  box-shadow: 0 12px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.1) !important;
}

/* Success notification styling */
.enhanced-notification.v-theme--light .v-snackbar--variant-flat.bg-success {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
  color: white !important;
}

/* Error notification styling */
.enhanced-notification.v-theme--light .v-snackbar--variant-flat.bg-error {
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%) !important;
  color: white !important;
}

/* Warning notification styling */
.enhanced-notification.v-theme--light .v-snackbar--variant-flat.bg-warning {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important;
  color: white !important;
}

/* Info notification styling */
.enhanced-notification.v-theme--light .v-snackbar--variant-flat.bg-info {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%) !important;
  color: white !important;
}
</style>

<style lang="scss">
@import '@/styles/main.scss';

.v-application {
  font-family: 'Roboto', sans-serif !important;
}

// Custom scrollbar
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>